import { type FC, useState } from "react";

import {
  Box,
  Column,
  Heading,
  Paragraph,
  SectionHeading,
  useToast,
} from "@hightouch/htui";
import { useSearchParams } from "src/router";

import hubspotLogo from "src/assets/partner-logos/hubspot.svg";
import { AuthLayout } from "src/components/auth/auth-layout";
import { PageSpinner } from "src/components/loading";
import { useAccessibleOrganizationsQuery } from "src/graphql";
import {
  PartnerConnectLogo,
  PartnerTermsCheckboxes,
} from "src/pages/partner-connect";
import { Workspaces } from "src/pages/workspaces/workspaces";

const PARTNER_NAME = "HubSpot";

const HubSpotMarketplaceInstall: FC = () => {
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get("returnUrl") ?? "";
  const { toast } = useToast();

  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [hasAcceptedSharing, setHasAcceptedSharing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { data: organizations, isLoading } = useAccessibleOrganizationsQuery(
    {},
    { select: (data) => data.getAccessibleOrganizations.organizations },
  );

  const handleSelectWorkspace = async (id: number) => {
    if (!hasAcceptedTerms || !hasAcceptedSharing) {
      toast({
        id: "hubspot-marketplace-terms-required",
        title: "Accept the terms first",
        message:
          "Both terms must be accepted before installing into a workspace.",
        variant: "error",
      });
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/hubspot/marketplace/sign-state`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ workspaceId: id, returnUrl }),
        },
      );
      if (!response.ok) {
        const errorBody = (await response.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(
          errorBody.error ?? `Server returned ${response.status}`,
        );
      }
      const data = (await response.json()) as { redirectUrl: string };
      window.location.href = data.redirectUrl;
    } catch (err) {
      setSubmitting(false);
      toast({
        id: "hubspot-marketplace-error",
        title: "Could not continue install",
        message: err instanceof Error ? err.message : "Please try again.",
        variant: "error",
      });
    }
  };

  if (isLoading || submitting) {
    return <PageSpinner />;
  }

  if (!returnUrl) {
    return (
      <AuthLayout metaTitle={`Connect Hightouch to ${PARTNER_NAME}`}>
        <Column align="center" gap={6} maxWidth="600px" mt={20} mx="auto">
          <Heading size="2xl">Invalid request</Heading>
          <Paragraph textAlign="center">
            This page must be opened from the HubSpot marketplace install flow.
          </Paragraph>
        </Column>
      </AuthLayout>
    );
  }

  const orgs = organizations ?? [];
  const orgsWithMemberWorkspaces = orgs
    .map((o) => ({
      ...o,
      memberWorkspaces: (o.workspaces ?? []).filter((w) => w?.is_member),
    }))
    .filter((o) => o.memberWorkspaces.length > 0);

  if (orgsWithMemberWorkspaces.length === 0) {
    return (
      <AuthLayout metaTitle={`Connect Hightouch to ${PARTNER_NAME}`}>
        <Column align="center" gap={6} maxWidth="600px" mt={20} mx="auto">
          <Heading size="2xl">No workspaces available</Heading>
          <Paragraph textAlign="center">
            You need a Hightouch workspace before installing {PARTNER_NAME}.
            Create one and try again.
          </Paragraph>
        </Column>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout metaTitle={`Connect Hightouch to ${PARTNER_NAME}`}>
      <Column gap={10} maxWidth="800px" mt={10} mx="auto" width="100%">
        <PartnerConnectLogo logo={hubspotLogo} name={PARTNER_NAME} />

        <Column gap={2} textAlign="center">
          <Heading size="xl">Connect Hightouch to {PARTNER_NAME}</Heading>
          <Paragraph>
            Choose a Hightouch workspace to install {PARTNER_NAME} into.
          </Paragraph>
        </Column>

        <PartnerTermsCheckboxes
          partnerName={PARTNER_NAME}
          hasAcceptedTerms={hasAcceptedTerms}
          hasAcceptedSharing={hasAcceptedSharing}
          onTermsChange={setHasAcceptedTerms}
          onSharingChange={setHasAcceptedSharing}
        />

        {orgsWithMemberWorkspaces.map((o) => (
          <Column key={o.slug} gap={2}>
            <Column>
              <Box
                color="text.tertiary"
                fontSize="sm"
                fontWeight="semibold"
                textTransform="uppercase"
              >
                Organization
              </Box>
              <SectionHeading mb={4}>{o.name ?? ""}</SectionHeading>
            </Column>
            <Workspaces
              workspaces={o.memberWorkspaces}
              onSelect={handleSelectWorkspace}
            />
          </Column>
        ))}
      </Column>
    </AuthLayout>
  );
};

export default HubSpotMarketplaceInstall;
